/**
 * Failures that are not bugs: something the user has to set up or bring back before the request
 * can run. They travel as a code (how the request failed) plus a reason (what is missing), so
 * every surface can say what is missing and offer the way to fix it instead of a raw message.
 */

export const PRECONDITION_REASONS = [
  /** No agent is installed or selected to run the conversation. */
  'no_agent',
  /** The agent runs, but is not signed in to its service. */
  'agent_signed_out',
  /** No installed plugin provides the models the request needs. */
  'no_model_provider',
  /** The model provider chosen in Settings is no longer installed. */
  'model_provider_missing',
  /** The workspace folder is not on this device (deleted, moved, unmounted, or elsewhere). */
  'folder_missing',
  /** The folder is not a Git repository, and the request needs one. */
  'not_a_repository',
  /** The request needs Crowdex Cloud and this server is not linked to it. */
  'cloud_unlinked',
  /**
   * Crowdex Cloud keeps no copy of the project the request opens there: its folder has to come
   * from a machine first (or the cloud starts from the repository's forge).
   */
  'cloud_project_missing',
] as const;

export type PreconditionReason = (typeof PRECONDITION_REASONS)[number];

/** `unconfigured`: the user has to set something up. `unavailable`: it is gone or unreachable. */
export type PreconditionCode = 'unconfigured' | 'unavailable';

export function isPreconditionReason(value: unknown): value is PreconditionReason {
  return typeof value === 'string' && (PRECONDITION_REASONS as readonly string[]).includes(value);
}

export class PreconditionError extends Error {
  override readonly name = 'PreconditionError';

  constructor(
    readonly code: PreconditionCode,
    readonly reason: PreconditionReason,
    message: string,
  ) {
    super(message);
  }
}

export function isPreconditionError(error: unknown): error is PreconditionError {
  return error instanceof PreconditionError;
}

/** The HTTP status of a precondition failure. */
export function preconditionStatus(code: PreconditionCode): number {
  return code === 'unconfigured' ? 424 : 503;
}

/**
 * The agents' own words for "not signed in", which they report as plain text: Claude Code's
 * `/login` hint, Codex's expired ChatGPT session or the API's 401, an ACP adapter's
 * `auth_required`. Only a turn's own failure is read this way, never a tool's output.
 */
const SIGNED_OUT_PATTERNS = [
  /\bplease run \/login\b/i,
  /\binvalid api key\b/i,
  /\bnot (?:signed|logged) in\b/i,
  /\bauthentication required\b/i,
  /\bauth_required\b/i,
  /\b(?:sign|log) in again\b/i,
  /\brefresh token (?:was|has been|is) (?:already used|expired|revoked|invalid)\b/i,
  /\bOAuth token has expired\b/i,
  // Codex and other OpenAI-format agents relay the API's refusal as it came.
  /\b401 Unauthorized\b/i,
  /\bmissing bearer or basic authentication\b/i,
  /\bincorrect api key provided\b/i,
  /\bauthentication_error\b/i,
];

/** Whether an agent failure means it is signed out of its service. */
export function isAgentSignedOutMessage(message: string): boolean {
  return SIGNED_OUT_PATTERNS.some((pattern) => pattern.test(message));
}
